import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Api from '../../services/Api';
import SecuredComponent from "./SiteEditElements/SecuredComponent";
import Person from "./Person";
import DimensionPicker from "./SiteEditElements/DimensionPicker";
import { DefaultButton, Link, MessageBar, MessageBarType, PrimaryButton, Separator, Spinner, SpinnerSize, Pivot, PivotItem, DocumentCardActivity, Icon, TooltipHost, TextField, CommandButton, Toggle, Checkbox } from "@fluentui/react";
import { PermissionRole, PowerAppBaseType, PowerAppChangeTaskType, PowerAppEnvironmentType, PowerAppProvisioningStatus, PowerAppsScope, PowerAppStatus } from "../../GlobalConst/globalEnums";
import RequestLicenseDialog from "./PowerAppEditElements/RequestLicenseDialog";
import PowerAppTasks from "./PowerAppTasks";
import { mapEnumToOptions, mapUserForPeople, renderDateTime } from "../../helpers/HelperMethods";
import PowerAppUsage from "./PowerAppEditElements/PowerAppUsage";
import SolutionDetails from "./PowerAppEditElements/SolutionDetails";
import CustomTextField from "./SiteEditElements/CustomTextField";
import LoadingIcon from "../formFields/LoadingIcon";
import CustomDropDown from "./SiteEditElements/CustomDropDown";
import axios from "axios";
import InfoTooltip from "../formFields/InfoTooltip";
import Warning from "../CreateSite/CommonComponents/Warning";

const CancelToken = axios.CancelToken;

const PowerAppEditor = ({ appId, onRequestClose, callBack, showSolutionModal, onEditChange, isEditable}) => {

    const tabs = {
        details: 'details',
        tasks: 'tasks',
        usage: 'usage',
        solution: 'solution'
    }
    const currentUser = useSelector((state) => state.user.currentUser)
    const cancelSource = useRef(null);
    
    const [app, setApp] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [editable, setEditable] = useState(isEditable);
    const [updateSet, setUpdateSet] = useState([]);
    const [myOwnApp, setMyOwnApp] = useState(true);
    const [error, setError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [messageVisible, setMessageVisible] = useState(false);
    const [licenseRequestCreated, setLicenseRequestCreated] = useState(false);
    const [licensesGranted, setLicensesGranted] = useState(false);
    const [childrenValidation, setChildrenValidation] = useState({})
    const [isChildrenUpdateValid, setIsChildrenUpdateValid] = useState(true);
    const [selectedTab, setSelectedTab] = useState(tabs.details)
    const [selectedSolution, setSelectedSolution] = useState(null)
    const [recertificationTask, setRecertificationTask] = useState()
    const [isRefreshed, setIsRefreshed] = useState(false)
    const [isRecertificationConfirmed, setIsRecertificationConfirmed] = useState(false)
    
    const getRecertificationTask = (tasks) =>{
        return tasks.find(x => x.type === PowerAppChangeTaskType.recertification)
    }

    useEffect(() => {
        cancelSource.current = CancelToken.source();

        getPowerApp(appId);
        
        return () => {
            cancelSource.current.cancel();
        }
    }, [appId]);

    useEffect(() => {
        setIsChildrenUpdateValid(Object.keys(childrenValidation).reduce((value, key) => {
            return value && childrenValidation[key];
        }, true))
    }, [childrenValidation])

    useEffect(() => {
      onEditChange(editable)
    }, [editable])

    const isMyApp = (app) => {
        return [app.primaryOwner, app.secondaryOwner].some(x => x && x.userId === currentUser.id) || app.isOwnedViaServiceLeads || app.isOwnedViaEnvironmentOwner
    };

    // const loadAppWithoutOwners = (app) => {
    //     let newApp = { ...app };
        
    //     newApp.primaryOwner = getValue('primaryOwner', true, app)
    //     newApp.secondaryOwner = getValue('secondaryOwner', true, app)

    //     setUpdateSet([])
    //     setApp(newApp);
    // }

    const getPowerApp = (id, loadApp = true) => {
        setIsLoading(true);
        Api.get(`/PowerAppsPanel?id=${id}`, { cancelToken: cancelSource.current.token })
            .then(res => res.data)
            .then(app => {
                setApp(app)

                if (app.solutions.length)
                    setSelectedSolution(app.solutions[0])

                setMyOwnApp(isMyApp(app));
                setIsLoading(false);

                setRecertificationTask(getRecertificationTask(app.changeTasks))

                if (callBack)
                    callBack(app)
            });
    };

    const onUpdateChildrenValidation = (key, isValid) => {
        setChildrenValidation(valid => ({ ...valid, [key]: isValid }))
    }

    const refresh = (loadApp = false) => {
        setLicenseRequestCreated(false);
        setLicensesGranted(false);
        getPowerApp(appId, loadApp);
        setIsRefreshed(true)
    };

    const updateChange = (key, value, callBack) => {
        let set = updateSet.filter(x => x.key !== key);
        if (app[key] === value || value === null) {
            setUpdateSet(set);
            if (callBack) callBack();
            return;
        }
        let myChange = { key: key, value: value };
        set.push(myChange);
        setUpdateSet(set);
        if (callBack) callBack();
    };

    const updatePersonChange = (key, value, isLimited, callBack) => {
        let set = updateSet.filter(x => x.key !== key);
        if (isLimited)
            value = value.length ? value[0] : null

        if ((app[key] || {}).userId === (value || {}).id) {
            setUpdateSet(set);
            if (callBack) callBack();
            return;
        }
        set.push({ key, value: isLimited ? {value} : value });
        setUpdateSet(set);
        if (callBack) callBack();
    }

    const updateDimensionChange = (key, keyId, value, idField = 'id', isNullableObject = false, callBack) => {
        setUpdateSet((prevSet) => {
            const isMultiValue = Array.isArray(value)

            let set = prevSet.filter(x => x.key !== key && x.key !== keyId);
            const parsedValue = isMultiValue
                                ? value.map(x => x[idField]) 
                                : isNullableObject
                                    ? (value ? {value: value[idField]} : {value: null})
                                    : (value ? value[idField] : 0)
            const parsedDefaultValue = isMultiValue 
                                        ? app[key].map(x => x[idField]) 
                                        : app[key]
                                            ? app[key][idField]
                                            : 0;
                            
            if (JSON.stringify(parsedDefaultValue) === JSON.stringify(parsedValue)) {
                if (callBack) callBack();
                    return set;
            }
            set.push({ key, value });
            set.push({ 
                key: keyId, 
                value: parsedValue
            });
            if (callBack) callBack();
            return set
        })
    }

    const showLicenseMessage = (action) => {
        if (action === 'licensesRequested') {
            setLicenseRequestCreated(true);
        } else if (action === 'licensesGranted') {
            setLicensesGranted(true);
        }
    };

    const editableClick = () => {
        setEditable(true);
    };

    const eraseSelection = () => {
        // let update = buildUpdate();
        // if (update.extension) {
        //     update.expirationDate = new Date(new Date().setFullYear(new Date().getFullYear() + update.extension)).toISOString();
        // }
        // setApp({ ...app, ...update });
        // setUpdateSet([]);
        setEditable(false);
        setError(false);
    };

    const buildUpdate = (recertify, restore) => {
        let command = { id: appId, type: app.type };
        updateSet.forEach(x => command[x.key] = x.value);

        command['recertify'] = !!recertify
        command['restore'] = !!restore

        return command;
    };

    const updateApp = (ev, recertify, restore) => {
        setError(false);
        setMessageVisible(false);
        setIsLoading(true);
        setIsSaving(true);
        eraseSelection();

        Api.post('UpdatePowerAppRequest', buildUpdate(recertify, restore))
            .then(res => res.data)
            .then(post => {
                setIsLoading(false);
                setIsSaving(false);
                if (post === 200) {
                    setMessageVisible(true);
                    refresh();
                } else {
                    setError(true);
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const saveEnabled = () => {
        return !isLoading
            && updateSet.length !== 0
            && !isSaving
            //&& (new Date(app.expirationDate) > Date.now() || getUpdatedValue('extension'))
            && isChildrenUpdateValid
            && !editEnabled()
            && !app.isPartOfSolution
            && !recertificationTask
            && editable
    };

    const isRecertification = () => {
        return !isLoading
            && !isSaving
            && !editEnabled()
            && !app.isPartOfSolution
            && recertificationTask
    };

    const isRestoreEnabled = () => {
        return !isLoading
            && !isSaving
            && !app.isPartOfSolution
            && !recertificationTask
            && app.provisioningStatus === PowerAppProvisioningStatus.expired
            && !isRefreshed
    }

    const editEnabled = () => {
        return !isRefreshed
            && !isSaving 
            && !editable 
            && app.status !== PowerAppStatus.deleted
            && !app.isPartOfSolution
            && (currentUser.role === PermissionRole.administrator || currentUser.role === PermissionRole.contributor
                || ![PowerAppProvisioningStatus.updating, PowerAppProvisioningStatus.pendingApproval, PowerAppProvisioningStatus.partiallyFailed, PowerAppProvisioningStatus.failed, PowerAppProvisioningStatus.permanentFailed, PowerAppProvisioningStatus.expired]
                        .includes(app.provisioningStatus)
                )
            && app.provisioningStatus !== PowerAppProvisioningStatus.expired
    };

    const getValue = (key, isNullableObject = false, defaultSet = null) => {
        const result = updateSet.find(x => x.key === key)
        if (result)
            return isNullableObject ? (result.value || {}).value : result.value
        else
            return defaultSet ? defaultSet[key] : app[key]
    }

    const getSecondaryOwnerError = () => {
        const primaryOwner = getValue('primaryOwner', true)
        const secondaryOwner = getValue('secondaryOwner', true)

        if (primaryOwner && primaryOwner.id) {
            const isSecondaryOwnerInvalid = !secondaryOwner || !secondaryOwner.id || secondaryOwner.isExternal || secondaryOwner.isServiceAccount;
        
            if (primaryOwner.isExternal && isSecondaryOwnerInvalid)
                return 'If Primary Owner is an External Account, Secondary Owner must be an ABB employee.'
        
            if (primaryOwner.isServiceAccount && isSecondaryOwnerInvalid)
                return 'If Primary Owner is a Service Account, Secondary Owner must be an ABB employee.'
        
            if (secondaryOwner && (primaryOwner.userId || primaryOwner.id) === (secondaryOwner.userId || secondaryOwner.id))
                return 'Secondary Owner must be different than Primary Owner.'
        }
    }

    const getEnvironmentPermissionWarning = (fieldName) => {
        const user = getValue(fieldName, true)
        if (user && user.powerPlatformEnvironments && !user.powerPlatformEnvironments.includes(app.environment.internalId)) {
             return <div>Please note that new owner might not have access to all environments where this {app.type} is deployed. Additional access to environment can be requested <a href='https://myservices.abb.com/cs_gr?id=esc_sc_cat_item_guide&table=sc_cat_item&sys_id=bc2842b187cb9590dc10eb580cbb35cf&searchTerm=request%20access%20environment'>here</a>.</div>
        }

        return null
    }

    const isFlow = (app) => app && app.type === PowerAppBaseType.flow

    const disabled = !editable;  

    //temporary solution to disable requesting licenses
    const enableLicenseRequests = false;

    const btnIconEdit = app && selectedTab === tabs.details &&
        <Icon style={{fontSize: 17}} iconName='edit' onClick={editableClick} />    

    const messageBar = 
        <div>
            {
                error &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.error}>
                    We cannot proceed your changes.
                </MessageBar>
            }
            {
                messageVisible &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.success}>
                    Your changes have been saved.
                </MessageBar>
            }
            {
                licenseRequestCreated &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.success}>
                    Your request has been created.
                </MessageBar>
            }
            {
                licensesGranted &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.success}>
                    License assignment has been confirmed.
                </MessageBar>
            }
        </div>

    const appTitle = app && 
        <div className="site-editor-header">
            <div className="site-editor-title" title={app.name}>
                {app.name.length > 53 ? `${app.name.substr(0, 50)}...` : app.name}
            </div>
            <div className="site-editor-icon" onClick={onRequestClose}><Icon iconName="chromeClose" /></div>
            {editEnabled() && <div className="site-editor-icon">{btnIconEdit}</div>}
            {messageBar}
        </div>

    const environmentUrl = app && `https://make.${!isFlow(app) ? 'powerapps' : 'powerautomate'}.com/environments/${app.environment.internalId}/home`
    const logo = <span style={{width: 48, height: 48, margin: '22px 18px 10px 10px'}} className={`cardLogo ${isFlow(app) ? 'flowLogo' : 'powerAppLogo'}`} />

    const btnRecertify = recertificationTask &&
        <TooltipHost content={isChildrenUpdateValid ? `Recertification will be valid for ${recertificationTask.additionalData}.` : 'Please provide all required fields to complete the recertification process.'}>
            <PrimaryButton className="edit-icon" onClick={(ev) => updateApp(ev, true)} disabled={!isChildrenUpdateValid || !isRecertificationConfirmed}>Recertify</PrimaryButton>
        </TooltipHost>

    const btnRestore =  
        <SecuredComponent 
            readSecurityLevel='Contributor'
            unlimitedAccess={app && (app.isOwnedViaServiceLeads || app.isOwnedViaEnvironmentOwner)}
        >
            {
                <TooltipHost>
                    <PrimaryButton className="edit-icon" onClick={(ev) => updateApp(ev, false, true)}>Restore</PrimaryButton>
                </TooltipHost>
            }
        </SecuredComponent>

    const getChange = (key) => updateSet.find(x => x.key === key)

    const isValid = (key) => childrenValidation[key] === undefined || childrenValidation[key]

    const isIcofrLimited = currentUser.role !== PermissionRole.administrator && app && app.environment.isIcofr

    const icofrWarningMessage = 
        isIcofrLimited &&
        <div>For ICoFR environments, ownership transfer must be requested by contacting support team: <a href={`mailto:global-storeandsharepowerplatformsupport@abb.com?subject=${`Ownership change requested for ${app.environment.name} ${app.type} ${app.name}`}`}>global-storeandsharepowerplatformsupport@abb.com</a>.</div>

    const ownerTooltipContent = 
        <div>
            Ownership transfer can be requested with or without granting access to artifacts.
            <ul>
                <li>
                    If permission update is not selected, the new owner will be updated in the provisioner database once the transfer is approved, but will not receive access to artifacts.
                </li>
                <li>
                    If a permission update is selected, the new owner will be granted access to the artifacts in the Power Platform environments upon approval.
                </li>
            </ul>
        </div>

    const recertificationCheckboxLabel =
        <div style={{marginLeft: 5}}>
            By Recertifying I confirm all information given is to the best of my understanding and I am adhering at all times to the Power Platform <a href="https://abb.sharepoint.com/sites/powerplatformhub/SitePages/KnowledgeBase/App-Maker-Agreement.aspx">App Maker Agreement</a> at ABB.
        </div>
    const recertificationCheckbox = app && isRecertification() &&
        <div style={{position: 'relative'}}>
            <Checkbox required={true} onRenderLabel={() => recertificationCheckboxLabel} defaultChecked={false} onChange={(ev, checked) => setIsRecertificationConfirmed(checked)} styles={{root:{marginRight: 30}}} />
            {
                !isRecertificationConfirmed &&
                <Warning element='nolabel-small' title='Select to complete Recertification' />
            }
        </div>
        

    const editor = app && 
            <div>
                <div className="site-editor" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ width: '100%', display: 'flex' }}>
                        {logo}
                        <div style={{width: '100%'}}>
                            <label className='item-title'>Name</label>
                            {
                                app.url
                                ? <Link
                                    target='_blank'
                                    href={app.url}
                                    styles={{ root: { display: 'block', padding: '8px', backgroundColor: 'rgb(243, 242, 241)' }}}
                                >
                                    {app.name}
                                </Link>
                                : <TextField
                                    autoComplete="off"
                                    type="text"
                                    disabled={true}
                                    value={app.name}
                                    // styles={{field: {color: 'rgb(50, 49, 48)'}}}
                                />
                            }
                        </div>
                    </div>
                    <div style={{ width: '100%' }}>
                        <label className='item-title'>Environment</label>
                        <Link
                            target='_blank'
                            href={environmentUrl}
                            styles={{ root: { display: 'block', padding: '8px', backgroundColor: 'rgb(243, 242, 241)' }}}
                        >
                            {app.environment.name}
                        </Link>
                    </div>
                    {
                        app.isPartOfSolution
                        ? <div>
                            <div style={twoColumnsContainer}>
                                <SecuredComponent readSecurityLevel='Reader'
                                    editSecurityLevel='Contributor'
                                    unlimitedAccess={myOwnApp}
                                >
                                    <div style={twoColumnsSingleCol}>
                                        <Person
                                            userType='Primary Owner'
                                            updateType="primaryOwner"
                                            disabled={true}
                                            user={app.primaryOwner}
                                            isRequired={true}
                                            limited={true}
                                        />
                                    </div>
                                </SecuredComponent>
                                <SecuredComponent readSecurityLevel='Reader'
                                    editSecurityLevel='Contributor'
                                    unlimitedAccess={myOwnApp}
                                >
                                    <div style={twoColumnsSingleCol}>
                                        <Person
                                            userType='Secondary Owner'
                                            updateType="secondaryOwner"
                                            disabled={true}
                                            user={app.secondaryOwner}
                                            isRequired={false}
                                            limited={true}
                                        />
                                    </div>
                                </SecuredComponent>
                            </div>
                          </div>
                        :
                        <div>
                            <div style={twoColumnsContainer}>
                                <SecuredComponent readSecurityLevel='Reader'
                                    editSecurityLevel={isIcofrLimited ? 'Administrator' : 'Contributor'}
                                    unlimitedAccess={myOwnApp && !isIcofrLimited}
                                    readOnlyAccess={myOwnApp && isIcofrLimited}
                                >
                                    <div style={twoColumnsSingleCol}>
                                        <Person
                                            userType={<div>Primary Owner <InfoTooltip content={ownerTooltipContent} /></div>}
                                            updateType="primaryOwner"
                                            onChange={(v) => updatePersonChange('primaryOwner', v, true)}
                                            disabled={disabled}
                                            user={getValue('primaryOwner', true)}
                                            isRequired={true}
                                            limited={true}
                                            onValidation={(v) => onUpdateChildrenValidation('primaryOwner', v)}
                                            includePowerPlatformEnvironments={app.environment.type !== PowerAppEnvironmentType.default}
                                            warningMessage={getEnvironmentPermissionWarning('primaryOwner') || (editable && icofrWarningMessage)}
                                        />
                                        {
                                            editable && getChange('primaryOwner') && isValid('primaryOwner') &&
                                            <Toggle 
                                                onText='Transfer ownership and update components permissions' 
                                                offText='Transfer only ownership' 
                                                defaultChecked={!app.environment.isIcofr} 
                                                onChange={(ev, checked) => updateChange('primaryOwnerTransferOnlyOwnership', !checked)} 
                                                disabled={app.environment.isIcofr}
                                            />
                                        }
                                    </div>
                                </SecuredComponent>
                                <SecuredComponent readSecurityLevel='Reader'
                                    editSecurityLevel={isIcofrLimited ? 'Administrator' : 'Contributor'}
                                    unlimitedAccess={myOwnApp && !isIcofrLimited}
                                    readOnlyAccess={myOwnApp && isIcofrLimited}
                                >
                                    <div style={twoColumnsSingleCol}>
                                        <Person
                                            userType={<div>Secondary Owner <InfoTooltip content={ownerTooltipContent} /></div>}
                                            updateType="secondaryOwner"
                                            onChange={(v) => updatePersonChange('secondaryOwner', v, true)}
                                            disabled={disabled}
                                            user={getValue('secondaryOwner', true)}
                                            isRequired={false}
                                            limited={true}
                                            onValidation={(v) => onUpdateChildrenValidation('secondaryOwner', v)}
                                            errorMessage={getSecondaryOwnerError()}
                                            includePowerPlatformEnvironments={app.environment.type !== PowerAppEnvironmentType.default}
                                            warningMessage={getEnvironmentPermissionWarning('secondaryOwner') || (editable && icofrWarningMessage)}
                                        />
                                        {
                                            editable && getChange('secondaryOwner') && isValid('secondaryOwner') &&
                                            <Toggle 
                                                onText='Transfer ownership and update components permissions' 
                                                offText='Transfer only ownership' 
                                                defaultChecked={!app.environment.isIcofr} 
                                                onChange={(ev, checked) => updateChange('secondaryOwnerTransferOnlyOwnership', !checked)} 
                                                disabled={app.environment.isIcofr}
                                            />
                                        }
                                    </div>
                                </SecuredComponent>
                            </div>
                            <div style={twoColumnsContainer}>
                                <SecuredComponent readSecurityLevel='Reader'
                                    editSecurityLevel='Contributor'
                                    unlimitedAccess={myOwnApp}
                                >
                                    <div style={twoColumnsSingleCol}>
                                        <DimensionPicker
                                            itemSelector={(v) => updateDimensionChange('geography', 'geographyId', v)}
                                            searchTitle="Country"
                                            searchDataUrl='/Geography'
                                            isDisabled={disabled}
                                            selection={getValue('geography')}
                                            isRequired={true}
                                            treeDataUrl='/Geography'
                                            includeCrossOrg={true}
                                            onValidation={(v) => onUpdateChildrenValidation('geography', v)}
                                        />
                                    </div>
                                </SecuredComponent>
                                <SecuredComponent readSecurityLevel='Reader'
                                    editSecurityLevel='Contributor'
                                    unlimitedAccess={myOwnApp}
                                >
                                    <div style={twoColumnsSingleCol}>
                                        <DimensionPicker
                                            itemSelector={(v) => updateDimensionChange('management', 'managementId', v)}
                                            searchTitle="Business owner organization"
                                            searchDataUrl='/Management'
                                            isDisabled={disabled}
                                            selection={getValue('management')}
                                            isRequired={true}
                                            treeDataUrl='/Management'
                                            onValidation={(v) => onUpdateChildrenValidation('management', v)}
                                        />
                                    </div>
                                </SecuredComponent>
                            </div>
                        </div>
                    }
                    <div>
                        <SecuredComponent readSecurityLevel='Reader'
                            editSecurityLevel='Contributor'
                            unlimitedAccess={myOwnApp}
                        >
                            <div style={{margin: '15px 0'}}>
                                <div style={{margin: '5px 0'}}>
                                    Status: {app.status}
                                    {
                                        app.provisioningStatus === PowerAppProvisioningStatus.expired &&
                                        <TooltipHost content='Artifact still exists on environment but recertification expired.' tooltipProps={{styles: {root: { axWidth: 400}}}}>
                                            <Icon iconName="Warning" className='warningIcon' />
                                        </TooltipHost>
                                    }
                                </div>
                                <div>
                                    {
                                        app.type === PowerAppBaseType.powerApp
                                        ? <div>
                                            <div>
                                                App Type: {app.appType}
                                            </div>
                                            {
                                                app.sharePointFormUrl &&
                                                <Link
                                                    target='_blank'
                                                    href={app.sharePointFormUrl}
                                                    styles={{ root: { display: 'block', marginTop: '8px', padding: '8px', backgroundColor: 'rgb(243, 242, 241)', overflowWrap: 'break-word' } }}
                                                >
                                                    {app.sharePointFormUrl}
                                                </Link>
                                            }
                                        </div>
                                        : <div>
                                            Flow State: {app.state}
                                        </div>
                                    }
                                </div>
                            </div>
                        </SecuredComponent>
                    </div>
                    <div style={{ width: '100%', marginTop: '10px', display: 'flex', justifyContent: 'space-between'}}>
                        {/* <div style={{width: 300}}>
                            <SecuredComponent readSecurityLevel='Reader'
                                editSecurityLevel='Contributor'
                                unlimitedAccess={myOwnApp}
                            >
                                <ExpirationDay
                                    onUpdate={updateChange}
                                    disabled={disabled}
                                    extension={getUpdatedValue('extension')}
                                    expirationDate={app.expirationDate}
                                />
                            </SecuredComponent>
                        </div> */}
                        <div style={{width: 300}}>
                            <SecuredComponent readSecurityLevel='Reader'
                                editSecurityLevel='Contributor'
                                unlimitedAccess={myOwnApp}
                            >
                                <label>Created date: </label>
                                <span  style={{color: 'blue'}}> {renderDateTime(app.created)}</span>
                            </SecuredComponent>
                        </div>
                        {/* <div style={{marginTop: '20px'}}>
                            <span>Number of Per App licenses: </span>
                            <PerAppLicenses app={app}/>
                        </div> */}
                    </div>
                    {
                        !app.isPartOfSolution &&
                        <div style={{marginTop: 25}}>
                            <SecuredComponent readSecurityLevel='Reader'
                                editSecurityLevel='Contributor'
                                unlimitedAccess={myOwnApp}
                            >
                                <label className='item-title'>Description <InfoTooltip content='The initial value is retrieved from the Power Platform object in the environment but will not be synchronized with the Power Platform object. It will remain accessible only through the Provisioner tool.' /></label>
                                <CustomTextField
                                    defaultValue={app.description}
                                    disabled={disabled}
                                    onUpdate={(v) => updateChange('description', v)}
                                    multiline={true}
                                />
                            </SecuredComponent>
                            <SecuredComponent readSecurityLevel='Reader'
                                editSecurityLevel='Contributor'
                                unlimitedAccess={myOwnApp}
                            >
                                <label className='item-title'>Dependencies <InfoTooltip content='Describe what other dependencies your solution/app/flow has (e.g. external systems dependencies, some specific environment configuration etc). Everything important that is not included inside component.' /></label>
                                <CustomTextField
                                    defaultValue={app.dependencies}
                                    disabled={disabled}
                                    onUpdate={(v) => updateChange('dependencies', v)}
                                    multiline={true}
                                />
                            </SecuredComponent>
                            <SecuredComponent 
                                readSecurityLevel='Reader'
                                editSecurityLevel='Contributor'
                                unlimitedAccess={myOwnApp}
                            >
                                <label className='item-title'>Scope</label>
                                <CustomDropDown
                                    options={mapEnumToOptions(PowerAppsScope)}
                                    defaultValue={app.scope}
                                    disabled={disabled}
                                    onUpdate={(v) => updateChange('scope', v)}
                                    required={true}
                                    onValidation={(v) => onUpdateChildrenValidation('scope', v)}
                                />
                            </SecuredComponent>
                        </div>
                    }
                    <Separator styles={separatorStyles} />
                    <div>
                        {recertificationCheckbox}
                        <div style={{...twoColumnsContainer, alignItems: 'center'}}>
                            <div style={twoColumnsSingleCol}>
                                <label className='item-title'>Last recertification:</label>
                                <DocumentCardActivity 
                                    activity={app.lastRecertificationDate ? `Last recertified on ${renderDateTime(app.lastRecertificationDate)}` : 'No recertification'} 
                                    people={mapUserForPeople(app.lastRecertifiedBy)}
                                />
                            </div>
                            {
                                isRecertification() && btnRecertify
                            }
                            {
                                isRestoreEnabled() && btnRestore
                            }
                        </div>
                    </div>
                    <Separator styles={separatorStyles} />
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                        {
                            <SecuredComponent readSecurityLevel='Administrator'
                                editSecurityLevel='Administrator'
                                unlimitedAccess={myOwnApp}>
                                <div style={{ width: '100%', display: "flex", justifyContent: "space-between"}}>
                                    <div>
                                    {
                                        editEnabled() && 
                                        <DefaultButton className="edit-icon" onClick={editableClick}> Edit </DefaultButton>
                                    }
                                    {
                                        editable && !isRecertification() &&
                                        <PrimaryButton className="edit-icon" onClick={updateApp} disabled={!saveEnabled()}> Save </PrimaryButton>
                                    }
                                    </div>
                                    {
                                        enableLicenseRequests &&
                                        <RequestLicenseDialog app={app} disableBtn={licenseRequestCreated} refresh={() => refresh()} onSubmit={(action) => showLicenseMessage(action)}/>
                                    }
                                </div>
                            </SecuredComponent>
                        }
                    </div>
                </div>
                <div className="edit-panel">
                    {
                        isSaving && 
                        <Spinner style={{ float: 'left', marginLeft: 20 }} size={SpinnerSize.medium} />
                    }
                </div>
            </div>

    const onSolutionsTabChange = (item) => {
        const solution = app.solutions.find(x => x.id === item.props.itemKey)
        setSelectedSolution(solution)
    }

    const getSolutionDetails = (solution) => 
            <SolutionDetails 
                solution={solution} 
                disabled={true}
                getValue={(key, isNullableObject) => getValue(key, isNullableObject, solution)}
            />

    const solutionsTabs = app && (
            app.solutions.length === 1
            ? getSolutionDetails(app.solutions[0])
            : <Pivot styles={{root: {maxWidth: 650, textWrap: 'wrap'}}} onLinkClick={onSolutionsTabChange} selectedKey={selectedSolution ? selectedSolution.id : undefined}>
                {
                    app.solutions.map(x =>
                        <PivotItem key={x.id} itemKey={x.id} headerText={x.name}>
                            {getSolutionDetails(x)}
                        </PivotItem>
                    )
                }
            </Pivot>
        )
    const premiumIcon = app && app.isPremium && 
            <div style={premiumIconStyles}>
                <TooltipHost content="Uses Premium Connectors">
                    <Icon iconName="diamond" />
                </TooltipHost>
            </div>
        
    const isSolutionEditable = selectedSolution 
        && ((selectedSolution.primaryOwner && selectedSolution.primaryOwner.userId === currentUser.id) || (selectedSolution.secondaryOwner && selectedSolution.secondaryOwner.userId === currentUser.id)
            || selectedSolution.isOwnedViaServiceLeads || selectedSolution.isOwnedViaEnvironmentOwner
            || currentUser.role === PermissionRole.administrator)
            
    const btnOpenSolutionForm = 
        selectedTab === tabs.solution && selectedSolution &&
        (
            selectedSolution.provisioningStatus === PowerAppProvisioningStatus.updating || selectedSolution.provisioningStatus === PowerAppProvisioningStatus.pendingApproval
            ? <TooltipHost content='Updates are in progress' styles={updatesIconStyles}>
                <Icon iconName='workflow' />
              </TooltipHost>
            : <div style={btnOpenSolutionFormStyles}>
                <TooltipHost content={!isSolutionEditable ? 'Only Solution Owners can edit Solution details.' : 'If your solution exists on multiple environments you will edit details on highest available environment.'}>
                    <CommandButton 
                        iconProps={{iconName: 'DocumentSet'}}
                        text='Edit in Solution'
                        onClick={() => showSolutionModal(selectedSolution.id)}
                        disabled={!isSolutionEditable}
                    />
                </TooltipHost>
              </div>
        )

    const _onTabChanged = (item, ev) => {
        setSelectedTab(item.props.itemKey)
    }

    return (
        isLoading && !app
        ? <LoadingIcon />
        :
        <div style={{height: '100%'}}>
            {appTitle}
            <div className='site-editor-container'>
                <div style={iconsContainerStyles}>
                    {btnOpenSolutionForm}
                    {premiumIcon}
                </div>
                <Pivot onLinkClick={_onTabChanged} selectedKey={selectedTab}>
                    <PivotItem itemKey={tabs.details} headerText="Details">
                        {editor}
                    </PivotItem>
                    <PivotItem itemKey={tabs.usage} headerText="Usage">
                        <PowerAppUsage app={app} />
                    </PivotItem>
                    {
                        app && app.isPartOfSolution && app.solutions.length && 
                        <PivotItem itemKey={tabs.solution} headerText="Solution details">
                            <div style={{paddingBottom: 20}}>
                                {solutionsTabs}
                            </div>
                        </PivotItem>
                    }
                    {
                        app && app.changeTasks.length && (currentUser.role === PermissionRole.administrator || app.isOwnedViaEnvironmentOwner) &&
                        <PivotItem itemKey={tabs.tasks} headerText="Tasks">
                            <PowerAppTasks app={app} refresh={() => refresh(true)} />
                        </PivotItem>
                    }
                </Pivot>
            </div>
        </div>
    );
}

// const sitesOwnerStyle = {
//     width: '100%',
//     marginTop: '10px',
//     //float: 'left',
//     marginRight: '70px'
// }

const iconsContainerStyles = {
    height: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'relative',
    right: 18,
    top: 12,
}

const premiumIconStyles = {
    cursor: 'default',
    fontSize: 22,
    zIndex: 1,
    marginLeft: 5
}

const btnOpenSolutionFormStyles = {
    zIndex: 1,
    marginTop: -8
}

const separatorStyles = {
    root: {
        margin: '10px 0'
    }
}

const twoColumnsContainer = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20,
}

const twoColumnsSingleCol = {
    flex: 1,
    overflow: 'hidden'
}

const updatesIconStyles = {
    root: {
        cursor: 'default',
        fontSize: 18,
        color: 'orange',
        margin: '2px 10px',
        zIndex: 1,
    }
}

export default PowerAppEditor


// WEBPACK FOOTER //
// ./src/components/EditSite/PowerAppEditor.js